import { DocumentOperationType } from 'store/documents/document';
import { Individual } from 'store/individuals/individual';
import { SecurityCheckStatus } from 'types/common';
import { SortDirections } from 'utils/tableHelpers';

export interface Customer {
    id: string;
    name: string;
    address: Address;
    website: string;
    email?: string;
    phone?: string;
    hasAisCertificate?: boolean;
    hasAesCertificate?: boolean;
    hasAepIcsCertificate?: boolean;
    hasAEP5DocumentUploaded?: boolean;
    customerType: CustomerType;
    brokerIds: string[];
    brokerOfIds: string[];
    eori: string;
    vat: string;
    referenceCode?: string;
    deferredAccountNumber?: string;
    specialInstructions?: string;
    traderAccountNumber?: string;
    vatNumber?: string;
    contact: CustomerContact;
    contacts?: CustomerContact[];
    customerDetails?: CustomerDetails;
    profilePictureLink?: string;
    active: boolean;
    numJobs?: number;
}

export enum DutyPayer {
    DECLARANT = 'DECLARANT',
    CONSIGNEE = 'CONSIGNEE',
}

export enum VatTreatment {
    STANDARD = 'STANDARD',
    POSTPONED = 'POSTPONED',
}

export const dutyPayerLabels: Record<DutyPayer, string> = {
    [DutyPayer.DECLARANT]: 'Declarant',
    [DutyPayer.CONSIGNEE]: 'Consignee',
};

export const vatTreatmentLabels: Record<VatTreatment, string> = {
    [VatTreatment.STANDARD]: 'Standard VAT (at Import)',
    [VatTreatment.POSTPONED]: 'Postponed VAT Accounting',
};

export interface CustomerDetails {
    id?: string;
    dutyPayer?: DutyPayer;
    confirmedTanUsage?: boolean;
    vatTreatment?: VatTreatment;
    vatExemption?: boolean;
    billingName?: string;
    billingEmail?: string;
    thirdParty?: string;
    billingCustomerCode?: string;
    aep5Verified?: boolean;
}

export interface CustomerContact {
    name?: string;
    email?: string;
    phoneNumber?: string;
    phoneCountryCode?: string;
    role?: string;
}

export interface Address {
    addressLine1: string;
    addressLine2?: string | null;
    postCode: string;
    city: string;
    country: string;
}

export enum CustomerType {
    TRADER = 'TRADER',
    CUSTOMS_BROKER = 'CUSTOMS_BROKER',
    HAULIER = 'HAULIER',
    FREIGHT_FORWARDER = 'FREIGHT_FORWARDER',
}

export const customerTypeLabels = [
    { id: CustomerType.TRADER, value: 'Trader ' },
    { id: CustomerType.CUSTOMS_BROKER, value: 'Customs Broker' },
    { id: CustomerType.HAULIER, value: 'Haulier' },
    { id: CustomerType.FREIGHT_FORWARDER, value: 'Freight Forwarder' },
] as const;

export enum CustomerSortParameter {
    ID = 'ID',
    NAME = 'NAME',
    TYPE = 'TYPE',
    EMAIL = 'EMAIL',
    PHONE = 'PHONE',
    EORI = 'EORI',
    REFERENCE_CODE = 'REFERENCE_CODE',
}

export type CustomerParams = {
    sortDirection: SortDirections;
    sortParameter: CustomerSortParameter;
    page: number;
    size: number;
    type: CustomerType;
    isRegistered: boolean;
    hasAisCertificates: boolean;
    hasAepIcsCertificates: boolean;
    hasAesCertificates: boolean;
    hasNctsCertificates: boolean;
    active: 'ACTIVE' | 'ARCHIVED';
    customerIds: string[];
    query: string;
    queryBy: string[];
};

export type DocumentResponse = {
    id: string;
    brokerId: string;
    link: string;
    contentType: string;
    fileName: string;
    fileSize: number;
    securityChecksStatus: SecurityCheckStatus;
    createdAt: string;
    creator: Individual;
    updatedAt: string;
    archived: boolean;
    archivedAt: string;
    activeFrom?: string;
    activeTo?: string;
};

export type CustomerFilePresignedUrlResponse = {
    customerId: string;
    preSignedUrl: string;
    operationType: DocumentOperationType;
};

export type ListCustomerFilesSortParams =
    | 'ID'
    | 'FILE_SIZE'
    | 'CONTENT_TYPE'
    | 'FILE_NAME'
    | 'CREATED_AT'
    | 'UPDATED_AT';

export type ListCustomerFilesParams = {
    page: number;
    size: number;
    sortDirection?: SortDirections;
    fileSortParameter?: ListCustomerFilesSortParams;
    orders?: string[];
    fileName?: string;
    contentType?: string[];
    creator?: string;
    archived?: boolean;
};
